import { useCallback } from "react";
import { Plus, Square } from "lucide-react";
import { useAdsWorkspaceStore, type SubAgentId } from "@/features/ads";
import { ADS_SUB_AGENTS, MARCUS_CHAT_AGENT } from "../../lib/chatAgentRegistry";
import { sanitizeAgentVProviderBranding } from "../../lib/providerBranding";
import type { ChatListRowAction, ChatListRowProps } from "../ChatListRow";
import { useAgentSessionsSummary } from "./useAgentSessionsSummary";
import { useSubAgentStatus } from "./useSubAgentStatus";

export interface ChatListRowModel extends ChatListRowProps {
  rowKey: string;
}

interface UseChatListRowsOptions {
  isLoading?: boolean;
  onStop?: () => void;
  showcaseAllAgents?: boolean;
  showcaseFocusAgentId?: string | null;
  onSelectMarcus?: () => void;
  onSelectSubAgent?: (agentId: SubAgentId) => void;
}

function agentRowHighlightId(agentId: string): string {
  return `companion-agent-row-${agentId}`;
}

function sanitizeSubAgentLastMessage(
  agentId: SubAgentId,
  lastMessage: ChatListRowProps["lastMessage"],
): ChatListRowProps["lastMessage"] {
  if (agentId !== "agent_v" || !lastMessage) return lastMessage;
  const text = sanitizeAgentVProviderBranding(lastMessage.text);
  return text === lastMessage.text ? lastMessage : { ...lastMessage, text };
}

const ONBOARDING_SHOWCASE_HIDDEN_SUB_AGENTS = new Set<SubAgentId>(["higgsfield", "higgsfield_cli"]);

export function useChatListRows({
  isLoading,
  onStop,
  showcaseAllAgents = false,
  showcaseFocusAgentId = null,
  onSelectMarcus,
  onSelectSubAgent,
}: UseChatListRowsOptions): {
  rows: ChatListRowModel[];
  hasSubAgentRows: boolean;
} {
  const selectChatAgent = useAdsWorkspaceStore((s) => s.selectChatAgent);
  const unreadCounts = useAdsWorkspaceStore((s) => s.unreadCounts);
  const lastMessagePreviews = useAdsWorkspaceStore((s) => s.lastMessagePreviews);
  const companionSessionControls = useAdsWorkspaceStore((s) => s.companionSessionControls);
  const { visibleAgents, getJobCount } = useSubAgentStatus();
  const subAgentSummary = useAgentSessionsSummary();

  const isBusy = !!(companionSessionControls?.isResolving || companionSessionControls?.isCreating);
  const createMarcusSession = companionSessionControls?.onCreateSession;

  // Marcus sessions are already workspace-scoped (filtered by coagent_type in
  // AdsWorkspace). The list is sorted newest-first, so [0] is the latest.
  const marcusSessions = companionSessionControls?.sessions ?? [];
  const marcusSessionCount = marcusSessions.length;
  const marcusLastUsedAt = marcusSessions[0]?.createdAt ?? null;
  const listedSubAgents = showcaseAllAgents
    ? ADS_SUB_AGENTS.filter((agent) => !ONBOARDING_SHOWCASE_HIDDEN_SUB_AGENTS.has(agent.subAgentId))
    : visibleAgents;

  const showcaseStateFor = (agentId: string): ChatListRowProps["showcaseState"] => {
    if (!showcaseAllAgents) return undefined;
    if (!showcaseFocusAgentId) return "visible";
    return showcaseFocusAgentId === agentId ? "featured" : "muted";
  };

  const handleSelectMarcus = useCallback(() => {
    if (onSelectMarcus) {
      onSelectMarcus();
      return;
    }
    selectChatAgent(null);
  }, [onSelectMarcus, selectChatAgent]);

  const handleNewMarcusChat = useCallback(async () => {
    if (!createMarcusSession || isBusy) return;
    if (onSelectMarcus) {
      onSelectMarcus();
    } else {
      selectChatAgent(null);
    }
    await createMarcusSession();
    if (onSelectMarcus) {
      onSelectMarcus();
    } else {
      selectChatAgent(null);
    }
  }, [createMarcusSession, isBusy, onSelectMarcus, selectChatAgent]);

  const marcusAction: ChatListRowAction | undefined =
    isLoading && onStop
      ? {
          icon: Square,
          onClick: onStop,
          title: "Stop response",
          tone: "danger",
        }
      : createMarcusSession
        ? {
            icon: Plus,
            onClick: handleNewMarcusChat,
            disabled: isBusy,
            title: "New chat",
          }
        : undefined;

  const rows: ChatListRowModel[] = [
    {
      rowKey: MARCUS_CHAT_AGENT.id,
      agent: MARCUS_CHAT_AGENT,
      lastMessage: lastMessagePreviews.marcus,
      unreadCount: unreadCounts.marcus,
      jobCount: 0,
      isStreaming: isLoading,
      sessionCount: marcusSessionCount,
      lastUsedAt: marcusLastUsedAt,
      onClick: handleSelectMarcus,
      action: marcusAction,
      dataHighlightId: agentRowHighlightId("marcus"),
      showcaseState: showcaseStateFor("marcus"),
    },
  ];

  for (const agent of listedSubAgents) {
    const summary = subAgentSummary[agent.subAgentId] ?? null;
    rows.push({
      rowKey: agent.subAgentId,
      agent,
      lastMessage: sanitizeSubAgentLastMessage(
        agent.subAgentId,
        lastMessagePreviews[agent.subAgentId],
      ),
      unreadCount: unreadCounts[agent.subAgentId],
      jobCount: getJobCount(agent.subAgentId),
      isStreaming: false,
      sessionCount: summary?.count,
      lastUsedAt: summary?.lastUsedAt ?? null,
      onClick: () => {
        if (onSelectSubAgent) {
          onSelectSubAgent(agent.subAgentId);
          return;
        }
        selectChatAgent(agent.subAgentId);
      },
      dataHighlightId: agentRowHighlightId(agent.subAgentId),
      showcaseState: showcaseStateFor(agent.subAgentId),
    });
  }

  return { rows, hasSubAgentRows: listedSubAgents.length > 0 };
}
